import { useRef, useState } from "react";
import { adminApi, API } from "../lib/api";
import { Upload, Loader2, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";

/**
 * Combined image input: paste URL or upload file directly.
 * - `value` — current image URL (full or relative /api/files/...)
 * - `onChange(url)` — called with the new URL string
 * - `password` — admin password for the upload endpoint
 */
export default function ImageInput({ value, onChange, password, label, testid }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image is larger than 8MB");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const client = adminApi(password);
      const res = await client.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // The API returns a path like /api/files/srishti/uploads/uuid.jpg
      // Convert to absolute URL so it works on every page.
      const abs = res.data.url.startsWith("http")
        ? res.data.url
        : `${API.replace(/\/api$/, "")}${res.data.url}`;
      onChange(abs);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      {label && (
        <span className="text-xs uppercase tracking-widest font-semibold text-[var(--srishti-text-secondary)] block">
          {label}
        </span>
      )}
      <div className="flex gap-2">
        <div className="flex-1 flex items-center bg-white border border-[var(--srishti-border)]">
          <LinkIcon size={14} className="ml-3 text-[var(--srishti-text-secondary)] shrink-0" />
          <input
            type="url"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://… or click upload →"
            className="w-full px-2 py-2 text-sm focus:outline-none bg-transparent"
            data-testid={testid ? `${testid}-url` : undefined}
          />
        </div>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="px-3 py-2 bg-[var(--srishti-maroon)] text-[#FDFBF7] text-xs uppercase tracking-widest font-semibold flex items-center gap-2 disabled:opacity-50 shrink-0"
          data-testid={testid ? `${testid}-upload` : undefined}
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {busy ? "Uploading…" : "Upload"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
        />
      </div>
      {value && (
        <img
          src={value}
          alt={label || "preview"}
          className="w-32 h-32 object-cover border border-[var(--srishti-border)]"
        />
      )}
    </div>
  );
}
